/** Admin page API */
import { getServersList } from "@/api/servers.js";
import { addStream, editStream, removeStream } from "@/api/streams.js";
import { getUrlParamsByKey, setUrlParam } from "@/utils/urls.js";
import { deleteUrlParam } from "../../utils/urls";

const UPDATE_INTERVAL = 5000;
const SERVER_URL_KEY = "server";

const state = {
  /** WSE servers list */
  servers: [],
  /** selected server ID */
  currentServerId: null,
  refreshToken: localStorage.getItem('refreshToken')
};

const mutations = {
  /** set servers list */
  setServers(state, servers) {
    state.servers = servers;
  },
  checkToken(state, refreshToken) {
    state.refreshToken = refreshToken;
  },
  /** set selected WSE server ID */
  setCurrentServerId(state, id) {
    state.currentServerId = id;
  },
  /** add new stream into server's streams list */
  addStreamData(state, stream) {
    /** find server */
    const server = state.servers.find(srv => srv.id == stream.serverId);

    /** if server exists */
    if (server) {
      /** add stream data to local */
      server.streams.push(stream);
    }
  },
  /** set new data for existing stream by it's id */
  setStreamData(state, stream) {
    const { stream: originStream, server, index } = getStreamPlacementById(
      stream.id
    );

    if (originStream) {
      server.streams.splice(index, 1, {
        ...originStream,
        ...stream
      });
    } else {
      console.warn(`Stream with ID <${stream.id}> not found`);
    }
  },
  /** delete one stream record by it's ID and ID of WSE server */
  removeStreamData(state, id) {
    const { stream, server, index } = getStreamPlacementById(id);

    if (stream) {
      server.streams.splice(index, 1);
    } else {
      console.warn(`Stream with ID <${id}> not found`);
    }
  }
};

const actions = {
  /** servers actions */
  /** init servers and start peridiocally updates */
  initServers({ dispatch }) {
    dispatch("updateServers");
    setInterval(() => dispatch("updateServers"), UPDATE_INTERVAL);
  },
  /** update servers list */
  async updateServers({ commit }) {
    const servers = await getServersList();
    commit("setServers", servers);
  },
  /** set current server ID and save it via API */
  setCurrentServer({ commit }, server) {
    const id = server == null ? null : server.id;
    commit("setCurrentServerId", id);
  },
  /** stream actions */
  /** create new stream */
  async addStream({ commit }, stream) {
    /** create stream via API call and get new stream ID */
    const { id, stream: streamName } = await addStream(stream);
    stream.id = id;
    stream.streamName = streamName;

    /** write stream to local storage */
    commit("addStreamData", stream);
  },
  /** edit stream URL */
  async editStream({ commit }, stream) {
    const matchObj = /(^.+)\.stream(_[a-zA-Z0-9]+)?$/.exec(stream.streamName);
    if (matchObj === null) {
      throw Error("Stream short name must end with '.stream' extension");
    }
    const shortName = matchObj[1];

    await editStream(stream.ndvrApp, shortName, stream.streamUrl, stream.streamDisplayName);
    commit("setStreamData", stream);
  },
  /** remove existng stream */
  async removeStream({ commit }, id) {
    await removeStream(id);
    commit("removeStreamData", id);
  }
};

/**
 * Find stream by it's ID
 * @param {Number} id Stream ID
 * @returns Object with 3 keys:
 *  stream - target stream object
 *  server - stream's server object
 *  index - index of target stream in server's streams list
 */
function getStreamPlacementById(id) {
  for (var i = 0; i < state.servers.length; i++) {
    const server = state.servers[i];
    for (var j = 0; j < server.streams.length; j++) {
      const stream = server.streams[j];

      if (stream.id === id) {
        return {
          stream: stream,
          server: server,
          index: j
        };
      }
    }
  }

  return {};
}

export default {
  namespaced: true,
  state,
  actions,
  mutations
};
